// src/components/Sales/CreateSalePage.jsx
import React, { useState, useEffect } from 'react';
import salesService from '../../services/salesService';
import { showNotification } from '../../utils/notification';
import printBill from '../../utils/printBill';
import './Sales.css';

const MAIN_WAREHOUSE_ID = 1; // ← change to your main warehouse ID

const CreateSalePage = ({ toggleSidebar }) => {

  // ─── Data ──────────────────────────────────────────────────────────────────
  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isLoadingStocks, setIsLoadingStocks] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Warehouse ─────────────────────────────────────────────────────────────
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [selectedInventoryName, setSelectedInventoryName] = useState('');

  // ─── Selections ────────────────────────────────────────────────────────────
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedRouteName, setSelectedRouteName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('Cash Customer');
  const [isCashCustomer, setIsCashCustomer] = useState(true);

  // ─── Product form ──────────────────────────────────────────────────────────
  const [productForm, setProductForm] = useState({
    categoryId: '',
    stockId: '',
    productId: '',
    qty: '1',
    unitPrice: ''
  });

  // ─── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);

  // ─── Return ────────────────────────────────────────────────────────────────
  const [returnAmount, setReturnAmount] = useState('');

  // ─── Payments ──────────────────────────────────────────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ type: 'CASH', amount: '' });

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadRoutes();
    loadInvoiceNumber();
    loadMainWarehouse();
  }, []);

  const loadMainWarehouse = async () => {
    try {
      const inventories = await salesService.getActiveInventories();
      const main = inventories.find(inv => inv.id === MAIN_WAREHOUSE_ID) || inventories[0];
      if (main) {
        setSelectedInventoryId(String(main.id));
        setSelectedInventoryName(main.name);
        await loadStocks(String(main.id));
      }
    } catch (err) {
      console.error('Failed to load warehouse:', err);
    }
  };

  const loadRoutes = async () => {
    try {
      const data = await salesService.getAvailableRoutes();
      setRoutes(data || []);
    } catch (err) {
      console.error('Failed to load routes:', err);
    }
  };

  const loadInvoiceNumber = async () => {
    try {
      const data = await salesService.getInvoiceNumber();
      setInvoiceNumber(data?.invoiceNumber || data?.data?.invoiceNumber || '');
    } catch (err) {
      console.error('Failed to load invoice number:', err);
    }
  };

  const loadStocks = async (inventoryId) => {
    try {
      setIsLoadingStocks(true);
      const stocks = await salesService.getStocksByInventory(inventoryId);
      const stockList = Array.isArray(stocks) ? stocks : [];
      setAllStocks(stockList);

      const categoryMap = new Map();
      stockList.forEach(item => {
        const cat = item.product?.category;
        if (cat?.id && item.qty > 0) {
          categoryMap.set(cat.id, { id: cat.id, name: cat.name });
        }
      });
      setFilteredCategories(Array.from(categoryMap.values()));
    } catch (err) {
      console.error('Failed to load stocks:', err);
    } finally {
      setIsLoadingStocks(false);
    }
  };

  // ─── Route → customers ─────────────────────────────────────────────────────
  const handleRouteChange = async (routeId) => {
    const route = routes.find(r => String(r.id) === String(routeId));
    setSelectedRouteId(routeId);
    setSelectedRouteName(route?.name || '');
    setSelectedCustomerId('');
    setSelectedCustomerName('Cash Customer');
    setIsCashCustomer(true);
    setCustomers([]);
    if (!routeId) return;
    try {
      setIsLoadingCustomers(true);
      const data = await salesService.getCustomersByRoute(routeId);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // ─── Category → products ───────────────────────────────────────────────────
  const handleCategoryChange = (categoryId) => {
    setProductForm(prev => ({ ...prev, categoryId, stockId: '', productId: '', qty: '1', unitPrice: '' }));
    if (!categoryId) { setFilteredProducts([]); return; }
    const products = allStocks.filter(item =>
      String(item.product?.category?.id) === String(categoryId) && item.qty > 0
    );
    setFilteredProducts(products);
  };

  const handleProductChange = (stockId) => {
    const stock = filteredProducts.find(p => String(p.id) === String(stockId));
    setProductForm(prev => ({
      ...prev,
      stockId,
      productId: String(stock?.product?.id || ''),
      unitPrice: stock ? String(stock.pricePerUnit) : '',
      qty: '1'
    }));
  };

  const getAvailableQty = () => {
    if (!productForm.stockId) return 0;
    return filteredProducts.find(p => String(p.id) === String(productForm.stockId))?.qty || 0;
  };

  // ─── Cart operations ───────────────────────────────────────────────────────
  const addToCart = (e) => {
    e.preventDefault();
    if (!productForm.stockId || !productForm.qty || !productForm.unitPrice) {
      showNotification('Please fill all product fields', 'error');
      return;
    }
    const stock = filteredProducts.find(p => String(p.id) === String(productForm.stockId));
    if (!stock) return;

    const requestedQty = parseInt(productForm.qty);
    if (requestedQty > stock.qty) {
      showNotification(`Only ${stock.qty} units available`, 'error');
      return;
    }

    setCart(prev => [...prev, {
      type: 'sale',
      stockId: stock.id,
      productId: stock.product?.id,
      productName: stock.product?.name || 'Unknown',
      categoryName: stock.product?.category?.name || '',
      qty: requestedQty,
      unitPrice: parseFloat(productForm.unitPrice),
      totalPrice: requestedQty * parseFloat(productForm.unitPrice)
    }]);

    setProductForm(prev => ({ ...prev, stockId: '', productId: '', qty: '1', unitPrice: '' }));
  };

  const removeFromCart = (index) => setCart(prev => prev.filter((_, i) => i !== index));

  // ─── Return operations ─────────────────────────────────────────────────────
  const addReturn = (e) => {
    e.preventDefault();
    const amt = parseFloat(returnAmount);
    if (isNaN(amt) || amt <= 0) {
      showNotification('Please enter a valid return amount', 'error');
      return;
    }

    const saleTotal = cart
      .filter(item => item.type === 'sale')
      .reduce((sum, item) => sum + item.totalPrice, 0);

    const existingReturn = cart.find(item => item.type === 'return');
    if (existingReturn) {
      showNotification('A return already exists. Remove it first to add a new one.', 'error');
      return;
    }

    if (amt > saleTotal) {
      showNotification('Return amount cannot exceed the sale total', 'error');
      return;
    }

    setCart(prev => [...prev, {
      type: 'return',
      productName: 'Sale Return (Deduction)',
      categoryName: '',
      qty: '-',
      unitPrice: 0,
      totalPrice: amt,
      amount: amt
    }]);

    setReturnAmount('');
  };

  // ─── Payment operations ────────────────────────────────────────────────────
  const addPayment = (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }
    const newAmount = parseFloat(paymentForm.amount);
    setPayments(prev => {
      const existing = prev.find(p => p.type === paymentForm.type);
      if (existing) {
        return prev.map(p =>
          p.type === paymentForm.type
            ? { ...p, amount: p.amount + newAmount }
            : p
        );
      }
      return [...prev, { ...paymentForm, amount: newAmount }];
    });
    setPaymentForm(prev => ({ ...prev, amount: '' }));
  };

  const removePayment = (index) => setPayments(prev => prev.filter((_, i) => i !== index));

  // ─── Calculations ──────────────────────────────────────────────────────────
  const saleItems = cart.filter(item => item.type === 'sale');
  const returnItem = cart.find(item => item.type === 'return');
  const subTotal = saleItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const returnTotal = returnItem ? returnItem.amount : 0;
  const grandTotal = subTotal - returnTotal;
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balance = totalPaid - grandTotal;

  // ─── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setSelectedRouteId('');
    setSelectedRouteName('');
    setSelectedCustomerId('');
    setSelectedCustomerName('Cash Customer');
    setIsCashCustomer(true);
    setFilteredProducts([]);
    setProductForm({ categoryId: '', stockId: '', productId: '', qty: '1', unitPrice: '' });
    setCart([]);
    setReturnAmount('');
    setPayments([]);
    setPaymentForm({ type: 'CASH', amount: '' });
    setCustomers([]);
    loadInvoiceNumber();
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    // ── Validation ──
    if (!selectedRouteId) { showNotification('Please select a route', 'error'); return; }
    if (!isCashCustomer && !selectedCustomerId) { showNotification('Please select a customer', 'error'); return; }
    if (saleItems.length === 0) { showNotification('Please add items to cart', 'error'); return; }
    if (payments.length === 0) { showNotification('Please add at least one payment', 'error'); return; }
    if (totalPaid < grandTotal) { showNotification('Total paid must cover the grand total', 'error'); return; }

    const payload = {
      customerId: isCashCustomer ? null : parseInt(selectedCustomerId),
      routeId: parseInt(selectedRouteId),
      inventoryId: parseInt(selectedInventoryId),
      products: saleItems.map(item => ({
        productId: parseInt(item.productId),
        qty: item.qty,
        pricePerUnit: item.unitPrice
      })),
      paymentMethods: payments.map(p => ({
        method: p.type,
        amount: p.amount
      })),
      productreturnData: returnItem ? { amount: returnItem.amount } : null
    };

    // ── Print FIRST (before any await so browser allows the popup) ──
    printBill({
      invoiceNumber,
      cart: saleItems,
      returnAmount: returnTotal,
      payments,
      subTotal,
      grandTotal,
      totalPaid,
      balance,
      customerName: selectedCustomerName,
      inventoryName: selectedInventoryName,
      routeName: selectedRouteName
    });

    // ── Then save to API ──
    try {
      setIsSaving(true);
      await salesService.create(payload);
      showNotification('Sale created successfully!', 'success');
      resetForm();
    } catch (error) {
      console.error('Error saving sale:', error);
      showNotification(`Failed to save: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="inventory-main-content">
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">☰</button>
        <h2 className="inventory-content-title">
          Create Sale
          {invoiceNumber && (
            <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '16px', color: '#666' }}>
              #{invoiceNumber}
            </span>
          )}
        </h2>
      </header>

      <div style={{ display: 'flex', gap: '24px', padding: '16px', alignItems: 'flex-start' }}>

        {/* ── LEFT ── */}
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Warehouse + Route + Customer */}
          <div className="product-selection-card">
            <div className="form-row-sales">
              <div className="form-col-sales">
                <label>Warehouse</label>
                <input
                  className="custom-input"
                  value={isLoadingStocks ? 'Loading...' : selectedInventoryName || 'Loading...'}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'default' }}
                />
              </div>

              <div className="form-col-sales">
                <label>Route</label>
                <select
                  className="custom-select"
                  value={selectedRouteId}
                  onChange={e => handleRouteChange(e.target.value)}
                >
                  <option value="">Select Route</option>
                  {routes.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-col-sales">
                <label>Customer</label>
                <select
                  className="custom-select"
                  value={isCashCustomer ? 'cash' : selectedCustomerId}
                  onChange={e => {
                    if (e.target.value === 'cash') {
                      setIsCashCustomer(true);
                      setSelectedCustomerId('');
                      setSelectedCustomerName('Cash Customer');
                    } else {
                      const customer = customers.find(c => String(c.id) === e.target.value);
                      setIsCashCustomer(false);
                      setSelectedCustomerId(e.target.value);
                      setSelectedCustomerName(customer?.name || 'Unknown');
                    }
                  }}
                  disabled={isLoadingCustomers}
                >
                  <option value="cash">Cash Customer</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {!selectedRouteId && <small style={{ color: '#999' }}>Select a route first</small>}
                {isLoadingCustomers && <small style={{ color: '#999' }}>Loading...</small>}
              </div>
            </div>

            {/* Category + Product */}
            <div className="form-row-sales" style={{ marginTop: '12px' }}>
              <div className="form-col-sales">
                <label>Category</label>
                <select
                  className="custom-select"
                  value={productForm.categoryId}
                  onChange={e => handleCategoryChange(e.target.value)}
                  disabled={isLoadingStocks || filteredCategories.length === 0}
                >
                  <option value="">{isLoadingStocks ? 'Loading...' : 'Select Category'}</option>
                  {filteredCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-col-sales">
                <label>Product</label>
                <select
                  className="custom-select"
                  value={productForm.stockId}
                  onChange={e => handleProductChange(e.target.value)}
                  disabled={!productForm.categoryId}
                >
                  <option value="">{!productForm.categoryId ? 'Select category first' : 'Select Product'}</option>
                  {filteredProducts.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.product?.name} (Available: {p.qty})
                    </option>
                  ))}
                </select>
                {productForm.stockId && (
                  <small className="availability-text">
                    Available: <strong>{getAvailableQty()}</strong> &nbsp;|&nbsp;
                    Unit Price: <strong>Rs. {filteredProducts.find(p => String(p.id) === String(productForm.stockId))?.pricePerUnit}</strong>
                  </small>
                )}
              </div>
            </div>

            {/* Qty + Price + Add */}
            <form onSubmit={addToCart}>
              <div className="form-row-sales" style={{ marginTop: '12px' }}>
                <div className="form-col-sales">
                  <label>Quantity</label>
                  <input
                    type="number"
                    className="custom-input"
                    value={productForm.qty}
                    onChange={e => setProductForm(prev => ({ ...prev, qty: e.target.value }))}
                    min="1"
                    max={getAvailableQty()}
                    disabled={!productForm.stockId}
                    required
                  />
                </div>
                <div className="form-col-sales">
                  <label>Unit Price (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={productForm.unitPrice}
                    onChange={e => setProductForm(prev => ({ ...prev, unitPrice: e.target.value }))}
                    disabled={!productForm.stockId}
                    required
                  />
                </div>
                <div className="form-col-sales" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-add-item-full"
                    disabled={!productForm.stockId}
                    style={{ width: '100%' }}
                  >
                    + Add to Cart
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Cart */}
          <div className="cart-display-card">
            <h4 className="card-title">Cart ({saleItems.length} items)</h4>
            {cart.length === 0 ? (
              <div className="empty-state">No items added yet.</div>
            ) : (
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Unit Price</th>
                    <th>Qty</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, i) => (
                    <tr key={i} style={item.type === 'return' ? { backgroundColor: '#fff3f3', color: '#c0392b' } : {}}>
                      <td>{item.productName}</td>
                      <td>{item.categoryName}</td>
                      <td>{item.type === 'return' ? '-' : `Rs. ${item.unitPrice.toFixed(2)}`}</td>
                      <td>{item.qty}</td>
                      <td style={item.type === 'return' ? { color: '#c0392b', fontWeight: 'bold' } : {}}>
                        {item.type === 'return'
                          ? `- Rs. ${item.amount.toFixed(2)}`
                          : `Rs. ${item.totalPrice.toFixed(2)}`}
                      </td>
                      <td>
                        <button className="btn-remove-mini" onClick={() => removeFromCart(i)} type="button">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Return */}
          <div className="product-selection-card">
            <h4 className="card-title" style={{ marginBottom: '10px' }}>
              Process Return
              {returnItem && (
                <span style={{ marginLeft: '12px', fontSize: '12px', color: '#c0392b', fontWeight: 'normal' }}>
                  ✓ Return of Rs. {returnItem.amount.toFixed(2)} applied
                </span>
              )}
            </h4>
            <form onSubmit={addReturn}>
              <div className="form-row-sales">
                <div className="form-col-sales">
                  <label>Return Amount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={returnAmount}
                    onChange={e => setReturnAmount(e.target.value)}
                    placeholder="Enter amount to deduct"
                    disabled={!!returnItem}
                  />
                  {returnItem && (
                    <small style={{ color: '#999' }}>Remove existing return to add a new one</small>
                  )}
                </div>
                <div className="form-col-sales" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-add-item-full"
                    disabled={!returnAmount || parseFloat(returnAmount) <= 0 || !!returnItem}
                    style={{ width: '100%', backgroundColor: '#e67e22', borderColor: '#e67e22' }}
                  >
                    Apply Return
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Payment */}
          <div className="payment-card">
            <h4 className="card-title">Payment</h4>

            <div className="payment-type-tabs">
              {['CASH', 'CHEQUE', 'CREDIT'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`payment-tab-btn ${paymentForm.type === type ? 'active' : ''}`}
                  onClick={() => setPaymentForm(prev => ({ ...prev, type }))}
                >
                  {type}
                </button>
              ))}
            </div>

            <form onSubmit={addPayment}>
              <div className="form-row-sales" style={{ marginTop: '10px' }}>
                <div className="form-col-sales">
                  <label>Amount (Rs.)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="custom-input"
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-col-sales btn-col">
                  <button type="submit" className="btn btn-add-payment">Add</button>
                </div>
              </div>
            </form>

            <div className="payments-list-section">
              {payments.length === 0 ? (
                <div className="empty-state">No payments added.</div>
              ) : (
                <table className="mini-table">
                  <thead>
                    <tr><th>Type</th><th>Amount</th><th></th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i}>
                        <td>{p.type}</td>
                        <td>Rs. {p.amount.toFixed(2)}</td>
                        <td>
                          <button className="btn-remove-mini" onClick={() => removePayment(i)} type="button">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Summary */}
            <div className="balance-summary">
              <div className="balance-row">
                <span>Sub Total:</span>
                <strong>Rs. {subTotal.toFixed(2)}</strong>
              </div>
              {returnTotal > 0 && (
                <div className="balance-row" style={{ color: '#c0392b' }}>
                  <span>Return Deduction:</span>
                  <strong>- Rs. {returnTotal.toFixed(2)}</strong>
                </div>
              )}
              <div className="balance-row" style={{ borderTop: '1px solid #eee', paddingTop: '6px', marginTop: '4px' }}>
                <span>Grand Total:</span>
                <strong>Rs. {grandTotal.toFixed(2)}</strong>
              </div>
              <div className="balance-row">
                <span>Total Paid:</span>
                <strong>Rs. {totalPaid.toFixed(2)}</strong>
              </div>
              <div className="balance-row">
                <span>{balance >= 0 ? 'Change:' : 'Still Owed:'}</span>
                <strong className={balance >= 0 ? 'positive' : 'negative'}>
                  Rs. {Math.abs(balance).toFixed(2)}
                </strong>
              </div>
            </div>
          </div>

          {/* Actions */}
          <button
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={isSaving || saleItems.length === 0 || payments.length === 0}
            style={{ width: '100%', padding: '12px', fontSize: '15px' }}
          >
            {isSaving ? 'Processing...' : 'Complete Sale & Print'}
          </button>

          <button
            className="btn btn-cancel"
            onClick={resetForm}
            disabled={isSaving}
            style={{ width: '100%' }}
          >
            Clear / Reset
          </button>
        </div>
      </div>
    </main>
  );
};

export default CreateSalePage;